import {
  GridColDef,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid-premium";
import { BurnNonSensitiveUi } from "../BurnNonSensitiveUi";
import { useMemo } from "react";
import { DurationAs } from "shared-ts-ui-time";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import IncompleteCircleOutlinedIcon from "@mui/icons-material/IncompleteCircleOutlined";
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import { useTranslation } from "react-i18next";
import { Tooltip, useTheme } from "@mui/material";
import { useColumns, UseColumnsReturnType } from "shared-ts-ui-x-data-grid";
import { OrganizationDto } from "../../organization/Organization";
import {
  SootingDegreeCalculationMethod,
  sootingDegreeCalculationMethods,
} from "../../chimney/flue/SootingDegreeCalculationMethod";

export const useBurnTableColumns = (
  multipleSelected: boolean,
  sootingDegreeCalculationMethod: SootingDegreeCalculationMethod,
  organization: OrganizationDto
): UseColumnsReturnType<BurnNonSensitiveUi> => {
  const { t } = useTranslation("firefly-shared-ts-domain");
  const theme = useTheme();
  const columns = useMemo(() => {
    const columns: Array<GridColDef<BurnNonSensitiveUi>> = [
      {
        field: "orderOfOccurence",
        headerName: "#",
        headerAlign: "right",
        width: 60,
        align: "right",
      },
      {
        field: "completed",
        headerName: t("Completed"),
        headerAlign: "center",
        width: 80,
        align: "center",
        valueGetter: (value, row) => !row.completed,
        renderCell: (params) => {
          if (params.row.completed) {
            return (
              <Tooltip title={t("Burn complete")}>
                <CircleRoundedIcon
                  sx={{
                    color: multipleSelected
                      ? params.row.color
                      : theme.palette.grey[500],
                    verticalAlign: "middle",
                    transition: "color 800ms ease",
                  }}
                />
              </Tooltip>
            );
          }
          return (
            <Tooltip title={t("Burn incomplete")}>
              <IncompleteCircleOutlinedIcon
                sx={{
                  color: multipleSelected
                    ? params.row.color
                    : theme.palette.grey[500],
                  verticalAlign: "middle",
                  transition: "color 800ms ease",
                }}
              />
            </Tooltip>
          );
        },
      },
      {
        field: "duration",
        headerName: t("Duration"),
        headerAlign: "right",
        width: 80,
        align: "right",
        renderCell: (params) => {
          return (
            <DurationAs
              duration={params.row.duration}
              unit={"hour"}
              decimalPrecision={1}
            />
          );
        },
      },
      {
        field: "startQuality",
        headerName: t("Burn Start"),
        description: t("Assessment of burn start"),
        align: "center",
        valueGetter: (value, row) =>
          t(`BurnAssessment.${row.analytics.startQuality}`),
      },
      {
        field: "burnHourCount",
        headerName: t("Burn Hours"),
        align: "right",
        width: 80,
        valueGetter: (value, row) => row.metrics.burnHourCount.toFixed(1),
      },
      {
        field: "temperatureMax",
        headerName: t("Max Temp."),
        headerAlign: "right",
        align: "right",
        valueGetter: (value, row) => row.metrics.temperatureMax,
        renderCell: (params) => {
          return params.row.metrics.chimneyFire ? (
            <>
              <LocalFireDepartmentRoundedIcon
                sx={{ scale: 0.8, verticalAlign: "middle" }}
              />
              {`${params.row.metrics.temperatureMax} °C`}
            </>
          ) : (
            `${params.row.metrics.temperatureMax} °C`
          );
        },
      },
      {
        field: "sootIndex",
        headerName: t("Soot Index"),
        align: "right",
        width: 90,
        valueGetter: (value, row) => row.metrics.sootIndex.toFixed(1),
      },
      {
        field: "sootingDegree",
        headerName: t("Sooting Degree"),
        align: "right",
        width: 80,
        valueGetter: (value, row) =>
          sootingDegreeCalculationMethod ===
          sootingDegreeCalculationMethods.sootIndex
            ? (
                (row.metrics.sootIndex / organization.sootIndexThreshold) *
                100
              ).toFixed(2)
            : (
                (row.metrics.burnHourCount /
                  organization.burnHourCountThreshold) *
                100
              ).toFixed(2),
        renderCell: (params) => `${params.value} %`,
      },
      {
        field: "deltaTemperature",
        headerName: t("Delta Temperature"),
        headerAlign: "right",
        align: "right",
        width: 80,
        valueGetter: (value, row) => row.metrics.temperatureMax,
      },
    ];
    return columns;
  }, [multipleSelected, t, theme.palette.grey]);

  const defaultColumnVisibility: GridColumnVisibilityModel = {
    deltaTemperature: false,
    burnHourCount: false,
  };

  return useColumns({
    tableId: "BurnTable",
    columns: columns,
    defaultColumnVisibility,
  });
};
